import { PureComponent, ReactNode } from 'react';
import Select, {
    components,
    ControlProps,
    IndicatorProps,
    MenuProps,
    StylesConfig,
} from 'react-select';
import { CSSObject } from '@emotion/serialize';
import { getUnit } from '../../../../store/utils';

const CREATE_UNITS = [
    { label: 'Days', getValue: (p: number) => p * 1 },
    { label: 'Weeks', getValue: (p: number) => p * 7 },
    { label: 'Months', getValue: (p: number) => p * 30 },
];

type Item = {
    label: string;
    value: number;
};

type State = {
    options: Item[];
    value: Item | null;
};

type Props = {
    onSelect: (item: Item | null) => void;
    value: Item | null;
};

const DropdownIndicator = (props: IndicatorProps<Item, false>) => {
    const { selectProps } = props;
    const { value } = selectProps;
    return !value ? (
        <components.IndicatorsContainer {...props}>
            <svg
                width="20"
                height="20"
                viewBox="0 0 28 28"
                className="cursor-pointer downarrow fill-neutral-5"
                preserveAspectRatio="xMidYMid meet"
            >
                <path d="M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z"></path>
            </svg>
        </components.IndicatorsContainer>
    ) : null;
};

const ClearIndicator = (props: IndicatorProps<Item, false>) => {
    return (
        <components.ClearIndicator {...props}>
            <svg
                width="16"
                height="16"
                viewBox="0 0 28 28"
                className="cursor-pointer close fill-neutral-5"
                preserveAspectRatio="xMidYMid meet"
            >
                <path d="M12,2C17.53,2 22,6.47 22,12C22,17.53 17.53,22 12,22C6.47,22 2,17.53 2,12C2,6.47 6.47,2 12,2M15.59,7L12,10.59L8.41,7L7,8.41L10.59,12L7,15.59L8.41,17L12,13.41L15.59,17L17,15.59L13.41,12L17,8.41L15.59,7Z"></path>
            </svg>
        </components.ClearIndicator>
    );
};

const IndicatorSeparator = () => null;
const NoOptionsMessage = () => null;

const Control = (props: ControlProps<Item, false>) => {
    return (
        <components.Control
            {...props}
            className="flex border border-neutral-7 focus-within:border-neutral-1 rounded-4"
        >
            {props.children}
        </components.Control>
    );
};

const Menu = (props: MenuProps<Item, false>) => {
    const { options } = props;
    if (options.length === 0) {
        return null;
    }
    return <components.Menu {...props}>{props.children}</components.Menu>;
};

const customComponents = {
    ClearIndicator,
    DropdownIndicator,
    IndicatorSeparator,
    NoOptionsMessage,
    Menu,
    Control,
};

const customStyles: StylesConfig<Item, false> = {
    control: () => {
        return {} as CSSObject;
    },
};

class FollowUpInput extends PureComponent<Props, State> {
    state: State = {
        options: [],
        value: this.props.value,
    };

    componentDidUpdate = (prevProps: Props): void => {
        if (prevProps.value !== this.props.value) {
            this.setState({ value: this.props.value });
        }
    };

    onInputChange = (newValue: string): void => {
        this.setState({
            options: generateOptions(newValue),
        });
    };

    onSelect = (value: Item | null): void => {
        this.setState({
            value,
            options: value ? generateOptions(value.label) : [],
        });

        this.props.onSelect(value);
    };

    render(): ReactNode {
        const { options, value } = this.state;
        return (
            <Select
                value={value}
                isClearable
                options={options}
                components={customComponents}
                styles={customStyles}
                onInputChange={this.onInputChange}
                placeholder="e.g. 3 Days"
                onChange={this.onSelect}
                tabSelectsValue={true}
            />
        );
    }
}

export default FollowUpInput;

function generateOptions(newValue: string): Item[] {
    const t = newValue.trim();

    if (!t) {
        return [];
    }

    const n = t.split(' ');

    const pi = parseInt(n[0]);

    if (isNaN(pi)) {
        return [];
    }

    return CREATE_UNITS.map((c) => ({
        label: `${pi} ${getUnit(c.label, pi.toString())}`,
        value: c.getValue(pi),
    }));
}
